import { memo, useEffect, useMemo } from 'react';
import { useFormContext, get } from 'react-hook-form';

import { DynamicDateInput } from 'components/common/Inputs';
import { toLowerCamelCase } from 'utils/string';
import type { SourceType } from 'types';
import type { FormItemUiDefinition } from '../../../../types/formItem.ts';
import { getDateMaskObject } from 'utils/dateAndTime.ts';

interface Props {
  onChange?: () => void;
  onBlur?: () => void;
  hasSubmitAttempt?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const DateItem = memo(({ onChange, onBlur, hasSubmitAttempt = false, formItemUiDefinition, ...formItem }: Props) => {
  const {
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, formItem.registerName);
  const isLightError = Boolean(error) && !hasSubmitAttempt && !formItem?.disabled;
  const isHeavyError = Boolean(error) && hasSubmitAttempt && !formItem?.disabled;

  useEffect(() => {
    trigger(formItem.registerName);
  }, [formItem.registerName, trigger]);

  const attributes = useMemo(() => {
    const item = formItem.definition;

    const values = item[toLowerCamelCase(item.type)];

    const dateMaskObject = getDateMaskObject(values.dateMask);

    const getDateFromSource = (source: SourceType, id: string) => {
      switch (source) {
        case 'DateNow':
          return new Date();
        case 'CreatedDate':
          return formItem.createdAt;
        case 'UpdatedDate':
          return formItem.updatedAt;
        case 'FormItem':
          return watch(id);
        default:
          return null;
      }
    };

    const minDate = getDateFromSource(values.fromSourceType as SourceType, values.fromFormItemId) || values.fromDate;
    const maxDate = getDateFromSource(values.toSourceType as SourceType, values.toFormItemId) || values.toDate;

    return {
      ...formItem.registerProps,
      dateMask: dateMaskObject,
      minDate,
      maxDate,
    };
  }, [formItem, watch]);

  return (
    <DynamicDateInput
      disabled={formItem?.disabled}
      registerName={formItem.registerName}
      registerProps={attributes}
      error={isHeavyError ? error : undefined}
      lightError={isLightError}
      formItemUiDefinition={formItemUiDefinition}
      onDateChange={() => {
        onChange?.();
      }}
      onBlur={onBlur}
    />
  );
});
